import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AddEditAdUnitContext } from 'features/inventory/ad-unit/contexts/AddEditAdUnitContext'
import { useAbility } from 'providers/casl'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { EditSchema } from '../../schemas/editSchema'
import { mapper } from './api/mapper'
import { UPDATE_AD_UNIT } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseUpdateAdUnit } from './types'

export const useUpdateAdUnit = (): UseUpdateAdUnit => {
  const ability = useAbility()
  const { t } = useTranslation('features/adUnit')
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading, setTouched } = useContext(AddEditAdUnitContext)
  const [errors, setErrors] = useState<FieldErrors<EditSchema>>({})

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_AD_UNIT,
    onCompleted: async (data: Data) => {
      const {
        updateAdUnit: { errors },
      } = data

      setTouched(false)

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.error.update'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('form.success.update'), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['adUnit'],
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const updateAdUnit = (data: EditSchema): void => {
    mutateAsync({
      input: mapper(data, ability),
    })
  }

  return { errors, updateAdUnit }
}
